import * as React from "react";
import { useDebouncedValue } from "@glide/common";
import type { WireDataPlotChartProps } from "../wire-data-plot/wire-data-plot";
import type { NewTableProps } from "../../components/new-table/new-table";
import type { DataPlotRowData, WireDataPlotDataPoint } from "@glide/fluent-components/dist/js/base-components";
import { computeDisplaySuggestionsForJSONData, type SingleDataPointView } from "./display-suggestions";
import type { AgentCreator } from "./agent-client";

export type ChartingProps = WireDataPlotChartProps & { chartDisplayType: string; chartLabel: string };
export type DiscoveryChart = {
    data: DataPlotRowData[];
    columnsToDisplay: WireDataPlotDataPoint[];
    showYAxisLabels: boolean;
    showLegend: boolean;
    graphTitle: string;
};

export type TableProps = NewTableProps & { tableLabel: string };
export type DiscoveryTableState = TableProps;

interface WireDiscoveryContextValue {
    searchInputValue: string;
    searchHistory: string[];
    charts: DiscoveryChart[] | undefined;
    table: DiscoveryTableState | undefined;
    singleDataPointView: SingleDataPointView | undefined;
    onSearchInputChange: (value: string) => void;
    onSubmitNewPrompt: () => void;
    isAgentRequestInFlight: boolean;
    isResponseError: boolean;
    llmResponseMessage: string | undefined;
    llmStructuredResponse: LLMStructuredResponse | undefined;
}

type LLMStructuredResponse = {
    summary: string;
    sql: string;
    insights: string;
    assumptions: string;
    trends: Array<{ label: string; value: string }>;
};

interface WireDiscoveryProviderProps {
    children: React.ReactNode;
    agentServiceClientCreator: AgentCreator;
}

const WireDiscoveryContext = React.createContext<WireDiscoveryContextValue | undefined>(undefined);

export const WireDiscoveryProvider: React.FC<WireDiscoveryProviderProps> = ({
    children,
    agentServiceClientCreator,
}) => {
    const [searchInputValue, setSearchInputValue] = React.useState("");
    const [searchHistory, setSearchHistory] = React.useState<string[]>([]);

    const [charts, setCharts] = React.useState<DiscoveryChart[] | undefined>();
    const [table, setTable] = React.useState<DiscoveryTableState | undefined>();
    const [singleDataPointView, setSingleDataPointView] = React.useState<SingleDataPointView | undefined>();

    const [llmResponseMessage, setLLMResponseMessage] = React.useState<string | undefined>(undefined);
    const [llmStructuredResponse, setLLMStructuredResponse] = React.useState<LLMStructuredResponse | undefined>(
        undefined
    );
    const [isAgentRequestInFlight, setIsAgentRequestInFlight] = React.useState(false);
    const [isResponseError, setIsResponseError] = React.useState(false);

    const promptTextInput = useDebouncedValue(searchInputValue, 300);

    const agentServiceRowDataCallbackHandler = React.useCallback((rows: Record<string, unknown>[]) => {
        // Called with agent result rows; agent-client.ts never calls it currently (its toolCallFinish hook is commented out)
        const tableAndSuggestions = computeDisplaySuggestionsForJSONData(rows as any);
        if (tableAndSuggestions === undefined) {
            // Since the LLM returns useful information we don't want to treat this an an error,
            // But there is nothing to do.
            return;
        }
        if (tableAndSuggestions.singleDataPointView) {
            setSingleDataPointView(tableAndSuggestions.singleDataPointView);
            return;
        }

        if (tableAndSuggestions.chartSuggestions.length > 0) {
            setCharts(tableAndSuggestions.chartSuggestions);
        }
        setTable(tableAndSuggestions.table);
    }, []);

    const agentServiceClient = React.useMemo(() => {
        if (agentServiceClientCreator === undefined) {
            return undefined;
        }
        // Consider refactoring this the nested callback changed the
        // original injection of the agent service client itself, maybe this is fine.
        return agentServiceClientCreator(agentServiceRowDataCallbackHandler);
    }, [agentServiceClientCreator, agentServiceRowDataCallbackHandler]);

    const onSubmitNewPrompt = React.useCallback(async () => {
        if (promptTextInput.length === 0 || agentServiceClient === undefined) {
            // nothing to do with no input nor agent.
            return;
        }

        setTable(undefined);
        setCharts(undefined);
        setSingleDataPointView(undefined);

        setIsResponseError(false);
        setLLMResponseMessage(undefined);
        setLLMStructuredResponse(undefined);
        setIsAgentRequestInFlight(true);

        setSearchHistory(prev => [...prev, searchInputValue]);

        try {
            // FIXME: Update to new API if/when we restore data agent.
            // agentServiceClient.reset();
            // for await (const message of agentServiceClient.run(
            //     {
            //         userMessage: promptTextInput,
            //     },
            //     new AbortController().signal
            // )) {
            //     if (message.type !== "message") continue;
            //     setIsAgentRequestInFlight(false);
            //     // This is a message from the agent, it now has a shape.
            //     setLLMResponseMessage(message.content);
            //     try {
            //         // eslint-disable-next-line no-console
            //         console.log("LLM response", message.content);
            //         setLLMStructuredResponse(JSON.parse(message.content));
            //     } catch (e: unknown) {
            //         // eslint-disable-next-line no-console
            //         console.error("Error parsing LLM response", e);
            //         setIsResponseError(true);
            //     }
            // }
        } catch (error: unknown) {
            // eslint-disable-next-line no-console
            console.error("Error consuming agent generator", error);
            setIsResponseError(true);
            setIsAgentRequestInFlight(false);
        }
    }, [agentServiceClient, promptTextInput, searchInputValue]);

    const handleSearchInputChange = React.useCallback((value: string) => {
        setSearchInputValue(value);
    }, []);

    return (
        <WireDiscoveryContext.Provider
            value={{
                searchInputValue,
                searchHistory,
                onSearchInputChange: handleSearchInputChange,
                onSubmitNewPrompt,
                isAgentRequestInFlight,
                isResponseError,
                llmResponseMessage,
                llmStructuredResponse,
                charts,
                table,
                singleDataPointView,
            }}>
            {children}
        </WireDiscoveryContext.Provider>
    );
};

export const useWireDiscovery = () => {
    const context = React.useContext(WireDiscoveryContext);
    if (context === undefined) {
        throw new Error("useWireDiscovery must be used within a WireDiscoveryProvider");
    }
    return context;
};
